import { useEffect, useState } from 'react';
import {
  FillLayerSpecification,
  Layer,
  MapRef,
  Source,
  useMapInstance,
} from 'shared/map-exports';
import { usePoisState } from 'state/usePoisState';
import { useEvacZoneGeometry } from 'features/evacZones/hooks/useEvacZoneGeometry';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import {
  SELECTED_POLYGON_FILL_PATTERN,
  SELECTED_POLYGON_STROKE_PAINT,
} from 'features/mapping/mapping.utils';
import {
  BELOW_STREETS_LAYER_ID,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles/constants';
import { INACTIVE_ZONES_LAYER_MIN_ZOOM } from './constants';
import { getSelectedNoStatusEvacZoneUId } from './utils';

const OVERLAY_SOURCE_ID = 'selected-evac-zone-overlay';
const OVERLAY_FILL_LAYER_ID = `${OVERLAY_SOURCE_ID}-fill`;
const OVERLAY_STROKE_LAYER_ID = `${OVERLAY_SOURCE_ID}-stroke`;

const OVERLAY_FILL_PAINT: FillLayerSpecification['paint'] = {
  'fill-pattern': SELECTED_POLYGON_FILL_PATTERN,
};

const isBelowTileCutoff = (map: MapRef | undefined): boolean =>
  map !== undefined && map.getZoom() < INACTIVE_ZONES_LAYER_MIN_ZOOM;

// Uses `zoom` over `zoomend`, which lags ~1s while the zoom eases out.
const useIsBelowTileCutoff = (): boolean => {
  const map = useMapInstance();
  const [isBelow, setIsBelow] = useState(() => isBelowTileCutoff(map));

  useEffect(() => {
    setIsBelow(isBelowTileCutoff(map));
  }, [map]);

  useMapEvent(
    'zoom',
    (event) => {
      setIsBelow(event.target.getZoom() < INACTIVE_ZONES_LAYER_MIN_ZOOM);
    },
    map,
    true,
  );

  return isBelow;
};

// Highlights a selected no-status zone below the zoom where the tiles drop it.
// From that zoom up, the tile highlight takes over.
export const SelectedEvacZoneOverlay = (): JSX.Element | null => {
  const { selectedPoi } = usePoisState();
  const uid = getSelectedNoStatusEvacZoneUId(selectedPoi);
  const enabled = useIsBelowTileCutoff();
  const zone = useEvacZoneGeometry({ uid, enabled });

  if (!uid || !zone) {
    return null;
  }

  return (
    <Source id={OVERLAY_SOURCE_ID} type="geojson" data={zone}>
      <Layer
        id={OVERLAY_FILL_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        type="fill"
        maxzoom={INACTIVE_ZONES_LAYER_MIN_ZOOM}
        paint={OVERLAY_FILL_PAINT}
      />
      <Layer
        id={OVERLAY_STROKE_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        type="line"
        maxzoom={INACTIVE_ZONES_LAYER_MIN_ZOOM}
        paint={SELECTED_POLYGON_STROKE_PAINT}
      />
    </Source>
  );
};
